import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { DillButton } from '../Dill/DillButton';
import { DillModal } from '../Dill/DillModal';
import { DillSelect } from '../Dill/DillSelect';

interface AppSaveStatementSnapshotModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (month: string) => void;
  loading?: boolean;
}

const buildMonthOptions = () => {
  const options: { label: string; value: string }[] = [];
  for (let i = 1; i <= 12; i++) {
    const m = moment().subtract(i, 'months');
    options.push({ label: m.format('MMMM YYYY'), value: m.format('YYYY-MM') });
  }
  return options;
};

const monthOptions = buildMonthOptions();

const AppSaveStatementSnapshotModal: React.FC<AppSaveStatementSnapshotModalProps> = ({
  open,
  handleClose,
  onSubmit,
  loading = false,
}) => {
  const prevOpenRef = useRef(false);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open && !wasOpen) {
      setSelectedMonth(monthOptions[0].value);
    }
  }, [open]);

  const selectedLabel = monthOptions.find((o) => o.value === selectedMonth)?.label ?? selectedMonth;

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Save End-of-Month Snapshots"
      maxWidth="min(90vw, 520px)"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={handleClose} />
          <DillButton
            label="Save Snapshots"
            onClick={() => onSubmit(selectedMonth)}
            isLoading={loading}
          />
        </>
      }>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Saves a statement snapshot for every active customer as of the end of{' '}
          <span className="font-medium text-gray-800">{selectedLabel}</span>. No emails will be sent
          — you can send these later using{' '}
          <span className="font-medium text-gray-800">Send All Statements</span>.
        </p>
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Statement Month
          </p>
          <DillSelect value={selectedMonth} onChange={setSelectedMonth} options={monthOptions} />
        </div>
        <p className="text-xs text-gray-400">
          Customers with no open balance will be skipped automatically.
        </p>
      </div>
    </DillModal>
  );
};

export default AppSaveStatementSnapshotModal;
